import type { ContentImage as SanityImageType } from '@/content/types';
import { buildSanityImageUrl, type SanityImageUrlOptions } from '@/lib/sanity-image';

export type { SanityImageType };

/**
 * The image object accepted by SanityImage components.
 *
 * ContentImage from @/content/types, whose asset is always resolved
 * (_id, url, metadata), is assignable to it. The type is wider than that: it
 * still tolerates a stored reference (asset with _ref, _type) and null fields,
 * for images that do not come through the domain types. At runtime the
 * components return null when asset._id is missing.
 */
export interface SanityImageSource {
  readonly asset?: {
    // Present on a resolved asset (asset->)
    readonly _id?: string | null;
    readonly url?: string | null;
    readonly metadata?: {
      readonly lqip?: string | null;
      readonly dimensions?: {
        readonly width?: number;
        readonly height?: number;
        readonly aspectRatio?: number;
      } | null;
    } | null;
    // Present only on a stored reference, which the domain types no longer carry
    readonly _ref?: string;
    readonly _type?: string;
    // Allow additional fields from either form
    readonly [key: string]: unknown;
  } | null;
  readonly hotspot?: SanityImageType['hotspot'];
  readonly crop?: SanityImageType['crop'];
  readonly alt?: string | null;
  readonly caption?: string | null;
}

export interface SanityImageProps {
  /** Expanded Sanity image from GROQ (with asset->) */
  readonly image: SanityImageSource;
  /** Explicit width in pixels */
  readonly width?: number;
  /** Explicit height in pixels */
  readonly height?: number;
  /** Additional CSS classes */
  readonly className?: string;
  /** Fill parent container (uses next/image fill mode) */
  readonly fill?: boolean;
  /** Alt text override (falls back to image.alt, then empty string) */
  readonly alt?: string;
  /** Disable lazy loading for above-the-fold / LCP images */
  readonly priority?: boolean;
  /** Custom sizes attribute for responsive images */
  readonly sizes?: string;
  /** Custom aspect ratio (e.g., "16/9") */
  readonly aspectRatio?: string;
  /** Quality 1-100 (default 80) */
  readonly quality?: number;
  /** Placeholder strategy (default "blur" when LQIP available) */
  readonly placeholder?: 'blur' | 'empty';
}

/**
 * Parse aspect ratio string (e.g., "16/9") to a numeric ratio.
 */
export function parseAspectRatio(ratio?: string): number | undefined {
  if (!ratio) return undefined;
  const parts = ratio.split('/').map(Number);
  const w = parts[0];
  const h = parts[1];
  if (w && h && h !== 0) return w / h;
  return undefined;
}

/**
 * Resolve the alt text from props and image data.
 * Priority: explicit alt prop > image.alt > "Decorative image".
 * Falls back to a non-empty string so crawlers never flag missing alt.
 */
export function resolveAlt(
  altProp: string | undefined,
  image: SanityImageSource,
): string {
  if (altProp !== undefined) return altProp;
  return image.alt || 'Decorative image';
}

/**
 * Return a11y attributes for an image based on its alt text.
 * Decorative images (alt="") get role="presentation" so crawlers
 * and screen readers treat them as intentionally non-descriptive.
 */
export function resolveImageA11y(alt: string): Record<string, string> {
  if (alt === '') {
    return { role: 'presentation', 'aria-hidden': 'true' };
  }
  return {};
}

/**
 * Extract intrinsic dimensions from the expanded asset metadata.
 */
export function getImageDimensions(image: SanityImageSource): {
  readonly width: number | undefined;
  readonly height: number | undefined;
  readonly aspectRatio: number | undefined;
} {
  const dims = image.asset?.metadata?.dimensions;
  return {
    width: dims?.width,
    height: dims?.height,
    aspectRatio: dims?.aspectRatio,
  };
}

/**
 * Build a Sanity CDN URL for the given image source.
 * Passes the full image object (including hotspot/crop) to @sanity/image-url.
 */
export function buildImageSrc(
  image: SanityImageSource,
  options: Pick<SanityImageUrlOptions, 'width' | 'height' | 'quality'> = {},
): string {
  // buildSanityImageUrl accepts SanityImageSource from @sanity/image-url,
  // which includes objects with { asset: { _id, url } } shape.
  return buildSanityImageUrl(image as Parameters<typeof buildSanityImageUrl>[0], options);
}

/**
 * Get the LQIP (Low Quality Image Placeholder) base64 data URL.
 */
export function getLqip(image: SanityImageSource): string | undefined {
  return image.asset?.metadata?.lqip ?? undefined;
}

/**
 * Compute the default sizes attribute when none is provided.
 */
export function computeDefaultSizes(
  sizes: string | undefined,
  fill: boolean | undefined,
  width: number | undefined,
): string {
  if (sizes) return sizes;
  if (fill) return '100vw';
  if (width) return `${width}px`;
  return '100vw';
}

/** Standard responsive breakpoints used for modern-format srcset generation. */
export const SRCSET_WIDTHS = [640, 750, 828, 1080, 1200, 1920] as const;

/**
 * Returns true when the asset is an SVG — no raster format conversion should apply.
 * Detects via asset.url extension (.svg) or asset._ref suffix (-svg).
 */
export function isSvgImage(image: SanityImageSource): boolean {
  const url = image.asset?.url;
  if (url && url.endsWith('.svg')) return true;
  const ref = image.asset?._ref;
  if (ref && ref.endsWith('-svg')) return true;
  return false;
}

/**
 * Build a srcset string for a specific modern format (avif or webp) at all SRCSET_WIDTHS.
 * URLs use an explicit fm= parameter so the Sanity CDN returns the requested format.
 */
export function buildModernSrcSet(
  image: SanityImageSource,
  format: 'avif' | 'webp',
  quality = 80,
): string {
  return SRCSET_WIDTHS.map(
    (w) =>
      `${buildSanityImageUrl(image as Parameters<typeof buildSanityImageUrl>[0], { width: w, quality, format })} ${w}w`,
  ).join(', ');
}

/**
 * Generate preload link props for a Sanity image.
 * Use in page `generateMetadata` to preload critical LCP images.
 *
 * @example
 * export async function generateMetadata() {
 *   const page = await getPage();
 *   return {
 *     other: {
 *       ...getImagePreloadProps(page.hero.image, { width: 1200 })
 *     }
 *   };
 * }
 */
export function getImagePreloadProps(
  image: SanityImageSource,
  options: {
    readonly width?: number;
    readonly aspectRatio?: string;
  } = {},
): Record<string, string> {
  if (!image.asset?._id) return {};

  const { width = 1200, aspectRatio } = options;
  const parsedRatio = parseAspectRatio(aspectRatio);
  const height = parsedRatio ? Math.round(width / parsedRatio) : undefined;

  const url = buildImageSrc(image, { width, height });

  const preloadWidths = [640, 750, 828, 1080, 1200, 1920, 2048].filter(
    (w) => w <= width * 2,
  );

  const srcset = preloadWidths
    .map((w) => {
      const h = parsedRatio ? Math.round(w / parsedRatio) : undefined;
      return `${buildImageSrc(image, { width: w, height: h })} ${w}w`;
    })
    .join(', ');

  return {
    'link:preload:hero': url,
    'link:preload:hero:as': 'image',
    'link:preload:hero:imagesrcset': srcset,
    'link:preload:hero:imagesizes': '100vw',
  };
}
